import { Action } from "redux";
import { ThunkAction } from "redux-thunk";
import { MATCHING_ENGINE, MEMBERS_PORTAL } from "../../accounts/Consts";
import IAppState from "../types/IAppState";

import { clearDataIngestionCache } from "../reducers/DataIngestionReducer";
import { clearHomeCache } from "../reducers/HomeReducer";
import { clearLookupCache } from "../reducers/LookupReducer";
import { clearRepertoireCache } from "../reducers/RepertoireReducer";

import { LOCAL_STORAGE_VERSION, SESSION_STORAGE } from "../StorageEngineKeys";
import { AccountSessionStorage } from "../services/AccountSessionStorage";
import { AppInsightsService } from "../../telemetry/services/AppInsightsService";

import {
  getEnabledModulesThunk,
  getHomeComponentDataThunk,
  getMenuItemsThunk,
  searchJobsThunk,
} from "./HomeThunks";

import { getLookupEntitiesThunk } from "./LookupThunks";

import {
  getCustomerThunk,
  getCustomerDateConfigurationThunk,
  getCustomerContributorTypesConfigurationThunk,
  getCustomerNamesTypesConfigurationThunk,
  getCustomerCatchUpAllocationsConfigurationThunk,
  getCustomerTimeZoneConfigurationThunk,
  getCustomerIsUsageTabDefaultSetToMatchConfigurationThunk,
  getMemberNumberConfigurationThunk,
  getRollUpTerritoriesConfigurationThunk,
  getAllocationCustomerTerritoryConfigurationThunk,
  bootstrapMembersPortalAccountContextThunk,
  getUsersThunk,
  getUserCRMValuesThunk,
} from "./AccountThunks";

import {
  getMyApplicationsThunk,
  getMyCasesThunk,
  getCRMIntegratedEnabledThunk,
  getViewAsAccessRequestsThunk,
  fetchMembersPortalUserRolesThunk,
} from "./MembersPortalThunks";

import { getShortCutKeysThunk, getUserPreferences } from "./RepertoireThunks";

type AppThunk = ThunkAction<Promise<void>, IAppState, null, Action<string>>;

interface IInitializeAppOnLoadArgs {
  portalType: string;
  currentUserEmail?: string;
}

/**
 * It is intended to run once when the app starts and the user
 * is authenticated.
 *
 * @param portalType - Identifies the active portal (e.g. members, matching engine)
 * @param currentUserEmail - Optional user email for members portal context
 */
export const initializeAppOnLoadThunk = ({
  portalType,
  currentUserEmail,
}: IInitializeAppOnLoadArgs): AppThunk => {
  return async (dispatch, getState) => {
    try {
		  if (portalType !== MATCHING_ENGINE) {
        await dispatch<any>(getViewAsAccessRequestsThunk(currentUserEmail));
      }
		
		
		await Promise.allSettled([
		dispatch<any>(getMenuItemsThunk(portalType)),
        dispatch<any>(getHomeComponentDataThunk()),
        dispatch<any>(getLookupEntitiesThunk()),
        dispatch<any>(getEnabledModulesThunk()),
        dispatch<any>(getCustomerThunk()),
        dispatch<any>(getCustomerDateConfigurationThunk()),
        dispatch<any>(getCustomerIsUsageTabDefaultSetToMatchConfigurationThunk()),
        dispatch<any>(getCustomerContributorTypesConfigurationThunk()),
        dispatch<any>(getCustomerNamesTypesConfigurationThunk()),
        dispatch<any>(getCustomerTimeZoneConfigurationThunk()),
        dispatch<any>(getCustomerCatchUpAllocationsConfigurationThunk()),
        dispatch<any>(getShortCutKeysThunk()),
        dispatch<any>(getUserPreferences()),
        dispatch<any>(getMemberNumberConfigurationThunk()),
        dispatch<any>(getRollUpTerritoriesConfigurationThunk()),
        dispatch<any>(getAllocationCustomerTerritoryConfigurationThunk()),
      ]);

      if (portalType !== MEMBERS_PORTAL) {
        const state = getState();

        await Promise.allSettled([
          dispatch<any>(getUsersThunk()),
          dispatch<any>(searchJobsThunk(state.home.jobSearchState)),
        ]);

        return;
      }

      if (currentUserEmail) {
        await dispatch<any>(bootstrapMembersPortalAccountContextThunk(currentUserEmail));
      } else {
        await dispatch<any>(getUserCRMValuesThunk());
      }

      await Promise.allSettled([
        dispatch<any>(fetchMembersPortalUserRolesThunk(portalType)),
        dispatch<any>(getCRMIntegratedEnabledThunk()),
      ]);

      const finalState = getState();

      if (finalState.membersPortal?.isCRMIntegrationEnabled) {
        await Promise.allSettled([
          dispatch<any>(getMyApplicationsThunk()),
          dispatch<any>(getMyCasesThunk()),
        ]);
      }
    } catch (error) {
      AppInsightsService.trackException(error, {
        properties: {
          action: "initializeAppOnLoadThunk",
          portalType,
          hasCurrentUserEmail: !!currentUserEmail,
        },
      });

      console.error("initializeAppOnLoadThunk failed:", error);
      throw error;
    }
  };
};

export const clearAppStateAndReinitializeThunk = ({
  portalType,
  currentUserEmail,
}: IInitializeAppOnLoadArgs): AppThunk => {
  return async (dispatch) => {
    window.localStorage.removeItem(LOCAL_STORAGE_VERSION);
    window.sessionStorage.removeItem(SESSION_STORAGE);
    AccountSessionStorage.clear();

    dispatch(clearDataIngestionCache());
    dispatch(clearHomeCache());
    dispatch(clearLookupCache());
    dispatch(clearRepertoireCache());
    await dispatch<any>(
      initializeAppOnLoadThunk({
        portalType,
        currentUserEmail,
      }),
    );
  };
};
